<template>
  <div class="teacher-courses" id="teacher-courses">
    <div class="teacher-courses__container">
      
      <!-- Фон -->
      <div class="teacher-courses__background" />

      <!-- Wrapper для свайпа (по образцу прототипа) -->
      <div class="teacher-courses__grid-wrapper">
        <div v-if="loading" class="teacher-courses__loading">
          <span class="teacher-courses__loading-spinner" aria-hidden="true" />
          <span class="teacher-courses__loading-text">Загрузка курсов...</span>
        </div>

        <div v-else-if="error" class="teacher-courses__error">{{ error }}</div>
        <!-- Track, который двигается с помощью translateX -->
        <div
          v-else
          class="teacher-courses__grid-track"
          :style="{ transform: `translateX(${translateX}%)` }"
        >
          <!-- Страницы с карточками курсов -->
          <div
            v-for="(page, pageIndex) in pages"
            :key="'page-' + pageIndex"
            class="teacher-courses__page"
          >
            <!-- Если страница пустая, показываем "Пусто" -->
            <div v-if="page.length === 0" class="teacher-courses__empty">
              <span class="teacher-courses__empty-text">Пусто</span>
              <img
                src="@/assets/beach-umbrella-svgrepo-com.svg"
                alt="Пусто"
                class="teacher-courses__empty-icon"
              />
            </div>

            <!-- Сетка карточек на странице -->
            <div v-else class="teacher-courses__grid">
              <!-- Динамическая генерация карточек курсов -->
              <div
                v-for="course in page"
                :key="course.id"
                class="teacher-courses__card"
                :class="{ 'teacher-courses__card--hidden': course.is_hidden }"
                @click="openCourse(course)"
              >
                <div class="teacher-courses__card-content">
                  <!-- бейдж "Скрыт", как было -->
                  <div
                    v-if="course.is_hidden"
                    class="teacher-courses__hidden-badge"
                  >
                    …
                  </div>

                  <!-- бейдж "Создано админом" -->
                  <div
                    v-if="isCreatedByAdmin(course)"
                    class="teacher-courses__admin-created-badge"
                    :title="`Создано: ${formatEditDate(course.created_at)}`"
                  >
                    Создано админом
                  </div>

                  <!-- бейдж "Переназначено админом" -->
                  <div
                    v-if="isReassignedByAdmin(course)"
                    class="teacher-courses__admin-reassigned-badge"
                    :title="`Переназначено: ${formatEditDate(
                      course.last_edited_at
                    )}`"
                  >
                    🔄 Переназначено админом
                  </div>

                  <!-- бейдж "Скрыто админом" -->
                  <div
                    v-if="isHiddenByAdmin(course)"
                    class="teacher-courses__admin-hidden-badge"
                    :title="`Скрыто: ${formatEditDate(course.last_edited_at)}`"
                  >
                    🔒 Скрыто админом
                  </div>

                  <!-- бейдж "Опубликовано админом" -->
                  <div
                    v-if="isPublishedByAdmin(course)"
                    class="teacher-courses__admin-published-badge"
                    :title="`Опубликовано: ${formatEditDate(
                      course.last_edited_at
                    )}`"
                  >
                    ✅ Опубликовано админом
                  </div>

                  <!-- бейдж "Изменено админом" -->
                  <div
                    v-if="isEditedByAdmin(course)"
                    class="teacher-courses__admin-edited-badge"
                    :title="`Последнее изменение: ${formatEditDate(
                      course.last_edited_at
                    )}`"
                  >
                    ✏️ Изменено админом
                  </div>

                  <!-- Изображение -->
                  <img
                    class="teacher-courses__image"
                    :class="`teacher-courses__image--${course.type}`"
                    alt="Course preview"
                    :src="course.image"
                    loading="lazy"
                    @error="handleImageError($event, course)"
                  />

                  <!-- Название -->
                  <p class="teacher-courses__title">{{ course.title }}</p>

                  <!-- Метаданные -->
                  <p class="teacher-courses__meta">
                    Категория: {{ getCourseCategoryLabel(course.category) }}
                    <span class="teacher-courses__meta-divider">|</span>
                    {{ course.modules }} модулей
                  </p>
                  <p class="teacher-courses__meta teacher-courses__meta--muted">
                    Всего участников: {{ course.participants }}
                  </p>

                  <!-- Статистика -->
                  <p class="teacher-courses__stats-line">
                    {{ course.completionRate }}% завершили
                    <span class="teacher-courses__meta-divider">|</span>
                    {{ course.homeworkRate }}% сдали ДЗ
                  </p>

                  <!-- Рейтинг -->
                  <div class="teacher-courses__rating">
                    <div class="teacher-courses__rating-stars">
                      <div class="teacher-courses__rating-stars-fill-svg">
                        <template v-for="starIndex in 5">
                          <svg
                            v-if="getRatingStarType(course.rating, starIndex) === 'full'"
                            :key="`full-${starIndex}`"
                            class="teacher-courses__rating-star"
                            viewBox="0 0 19 20"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M9.14258 0.538086L11.292 6.47461C11.4224 6.83448 11.7391 7.12939 12.1543 7.16797H12.1553L17.7285 7.68066C17.7338 7.68601 17.7464 7.70159 17.7549 7.73145C17.7696 7.78365 17.7614 7.82638 17.7461 7.85449L17.7295 7.87695L13.4775 12.0586C13.2318 12.2993 13.122 12.6408 13.1504 12.9707L13.1709 13.1113L14.4697 19.3633C14.4827 19.4258 14.4619 19.4645 14.4463 19.4854L9.69043 16.1504L9.55859 16.0723C9.28789 15.9386 8.97481 15.9386 8.7041 16.0723L8.57227 16.1504L3.81543 19.4854C3.79989 19.4643 3.7801 19.4253 3.79297 19.3633L5.0918 13.1113C5.15999 12.787 5.0888 12.4351 4.88184 12.168L4.78516 12.0586L0.533203 7.87695C0.509346 7.85349 0.487369 7.80068 0.506836 7.73145C0.515011 7.70256 0.527625 7.68655 0.533203 7.68066L6.10742 7.16797H6.1084C6.52346 7.12939 6.83931 6.83434 6.96973 6.47461L9.12012 0.538086C9.12359 0.528544 9.12776 0.521361 9.13086 0.515625C9.13402 0.521415 9.13902 0.528316 9.14258 0.538086Z"
                              fill="#FDB022"
                              stroke="#FDB022"
                            />
                          </svg>
                          <svg
                            v-else-if="getRatingStarType(course.rating, starIndex) === 'half'"
                            :key="`half-${starIndex}`"
                            class="teacher-courses__rating-star"
                            viewBox="0 0 21 23"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M10.5 18.9283V0.5C10.4152 0.50001 10.3304 0.522091 10.2522 0.566244C10.0724 0.667865 9.942 0.993652 9.6815 1.64523L7.53241 7.01956C7.45531 7.21237 7.41675 7.30878 7.35825 7.38294C7.30647 7.44856 7.24358 7.50126 7.17347 7.5378C7.09425 7.57906 7.00285 7.59157 6.82003 7.61657L1.72477 8.31341C1.10701 8.3979 0.798142 8.44014 0.658765 8.60607C0.537642 8.75027 0.481103 8.95098 0.50562 9.14976C0.533822 9.37848 0.762177 9.62203 1.2189 10.1093L4.98596 14.1276L4.98732 14.129C5.12157 14.2723 5.18884 14.3441 5.23176 14.4309C5.26987 14.5081 5.29389 14.5934 5.30234 14.6815C5.31188 14.7812 5.29394 14.8853 5.25807 15.0936L4.2581 20.8986C4.13686 21.6024 4.07624 21.9543 4.16999 22.1585C4.25145 22.3359 4.39946 22.46 4.57093 22.4945C4.76825 22.5342 5.03965 22.359 5.58248 22.0085L10.0597 19.1177L10.0608 19.117C10.2208 19.0137 10.3008 18.962 10.3858 18.9418C10.4234 18.9328 10.4617 18.9283 10.5 18.9283Z"
                              fill="#FDB022"
                            />
                            <path
                              d="M10.3858 18.9418C10.4611 18.9237 10.5388 18.9237 10.6142 18.9418C10.6993 18.9621 10.7796 19.0139 10.9403 19.1177L15.4176 22.0085C15.9603 22.359 16.2318 22.5342 16.429 22.4945C16.6005 22.46 16.7486 22.3359 16.83 22.1585C16.9238 21.9543 16.8632 21.6024 16.7419 20.8986L15.742 15.0936C15.706 14.8853 15.6881 14.7812 15.6977 14.6815C15.7061 14.5934 15.7301 14.5081 15.7682 14.4309C15.8114 14.3439 15.8789 14.2719 16.0141 14.1276L19.7811 10.1093C20.2378 9.62203 20.4662 9.37848 20.4944 9.14976C20.5188 8.95098 20.4624 8.75027 20.3413 8.60607C20.2019 8.44014 19.8929 8.3979 19.2753 8.31341L14.18 7.61657C13.9972 7.59157 13.9057 7.57906 13.8265 7.5378C13.7564 7.50126 13.6936 7.44856 13.6417 7.38294C13.5833 7.30878 13.5447 7.21237 13.4675 7.01956L11.3185 1.64523C11.0579 0.993652 10.9277 0.667865 10.7478 0.566244C10.5914 0.477919 10.4086 0.477919 10.2522 0.566244M10.3858 18.9418C10.3008 18.962 10.2208 19.0137 10.0608 19.117L10.0597 19.1177L5.58248 22.0085C5.03965 22.359 4.76825 22.5342 4.57093 22.4945C4.39946 22.46 4.25145 22.3359 4.16999 22.1585C4.07624 21.9543 4.13686 21.6024 4.2581 20.8986L5.25807 15.0936C5.29394 14.8853 5.31188 14.7812 5.30234 14.6815C5.29389 14.5934 5.26987 14.5081 5.23176 14.4309C5.18884 14.3441 5.12157 14.2723 4.98732 14.129L4.98596 14.1276L1.2189 10.1093C0.762177 9.62203 0.533822 9.37848 0.50562 9.14976C0.481103 8.95098 0.537642 8.75027 0.658765 8.60607C0.798142 8.44014 1.10701 8.3979 1.72477 8.31341L6.82003 7.61657C7.00285 7.59157 7.09425 7.57906 7.17347 7.5378C7.24358 7.50126 7.30647 7.44856 7.35825 7.38294C7.41675 7.30878 7.45531 7.21237 7.53241 7.01956L9.6815 1.64523C9.942 0.993652 10.0724 0.667865 10.2522 0.566244M10.3858 18.9418C10.4234 18.9328 10.4617 18.9283 10.5 18.9283V0.5C10.4152 0.50001 10.3304 0.522091 10.2522 0.566244"
                              stroke="#FDB022"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                            />
                          </svg>
                          <svg
                            v-else
                            :key="`empty-${starIndex}`"
                            class="teacher-courses__rating-star"
                            viewBox="0 0 19 20"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M9.14258 0.538086L11.292 6.47461C11.4224 6.83448 11.7391 7.12939 12.1543 7.16797H12.1553L17.7285 7.68066C17.7338 7.68601 17.7464 7.70159 17.7549 7.73145C17.7696 7.78365 17.7614 7.82638 17.7461 7.85449L17.7295 7.87695L13.4775 12.0586C13.2318 12.2993 13.122 12.6408 13.1504 12.9707L13.1709 13.1113L14.4697 19.3633C14.4827 19.4258 14.4619 19.4645 14.4463 19.4854L9.69043 16.1504L9.55859 16.0723C9.28789 15.9386 8.97481 15.9386 8.7041 16.0723L8.57227 16.1504L3.81543 19.4854C3.79989 19.4643 3.7801 19.4253 3.79297 19.3633L5.0918 13.1113C5.15999 12.787 5.0888 12.4351 4.88184 12.168L4.78516 12.0586L0.533203 7.87695C0.509346 7.85349 0.487369 7.80068 0.506836 7.73145C0.515011 7.70256 0.527625 7.68655 0.533203 7.68066L6.10742 7.16797H6.1084C6.52346 7.12939 6.83931 6.83434 6.96973 6.47461L9.12012 0.538086C9.12359 0.528544 9.12776 0.521361 9.13086 0.515625C9.13402 0.521415 9.13902 0.528316 9.14258 0.538086Z"
                              stroke="#FDB022"
                            />
                          </svg>
                        </template>
                      </div>
                    </div>

                    <span class="teacher-courses__rating-count">{{
                      course.rating || 0
                    }}</span>
                    <span class="teacher-courses__rating-reviews"
                      >({{ course.reviews }} отзывов)</span
                    >
                  </div>

                  <!-- Низ карточки: слева "Скрыть", справа кнопки "Удалить" и "Управление" -->
                  <div class="teacher-courses__footer">
                    <button
                      class="teacher-courses__hide-btn"
                      @click.stop="toggleCourseVisibility(course.id)"
                    >
                      <img
                        class="teacher-courses__hide-icon"
                        alt="Toggle"
                        :src="course.is_hidden ? iconShow : iconHide"
                      />
                      <span class="teacher-courses__hide-text">
                        {{ course.is_hidden ? "Показать" : "Скрыть" }}
                      </span>
                    </button>

                    <button
                      class="teacher-courses__map-btn"
                      @click.stop="handleOpenCourseMap(course)"
                    >
                      {{ '\u041a\u0430\u0440\u0442\u0430 \u043a\u0443\u0440\u0441\u0430' }}
                    </button>

                    <div class="teacher-courses__action-buttons">
                      <!-- Кнопка "Удалить" -->
                      <button
                        class="teacher-courses__delete-btn"
                        @click.stop="
                          handleDeleteCourse(
                            course.id,
                            course.title,
                            course.teacher_id
                          )
                        "
                        title="Удалить курс"
                      >
                        <svg
                          class="teacher-courses__delete-icon"
                          width="14"
                          height="14"
                          viewBox="0 0 14 14"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M1 3.5H13M5.5 6.5V10.5M8.5 6.5V10.5M2 3.5L3 12.5C3 13.0523 3.44772 13.5 4 13.5H10C10.5523 13.5 11 13.0523 11 12.5L12 3.5M5 3.5V1.5C5 0.947715 5.44772 0.5 6 0.5H8C8.55228 0.5 9 0.947715 9 1.5V3.5"
                            stroke="currentColor"
                            stroke-width="1.5"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          />
                        </svg>
                        <span class="teacher-courses__delete-text"
                          >Удалить</span
                        >
                      </button>

                      <!-- Кнопка "Управление" -->
                      <button
                        class="teacher-courses__manage-btn"
                        @click.stop="handleManageCourse(course.id)"
                      >
                        <img
                          class="teacher-courses__manage-icon"
                          alt="Manage"
                          src="@/assets/cdn-animaapp/Vector4.svg"
                        />
                        <span class="teacher-courses__manage-text"
                          >Управление</span
                        >
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Кнопки навигации (стрелки влево/вправо) -->
      <div class="teacher-courses__navigation">
        <!-- Кнопка "Влево" (предыдущая страница) -->
        <div
          class="teacher-courses__nav-btn"
          :class="{ 'teacher-courses__nav-btn--disabled': !hasPrevPage }"
          @click="prevPage"
        >
          <img
            class="teacher-courses__nav-btn-bg"
            alt="Navigation button"
            src="/assets/animaapp/rectangle-86.svg"
          />
          <!-- Стрелка влево -->
          <div class="teacher-courses__nav-btn-icon">
            <svg width="10" height="16" view="0 0 10 16" fill="none">
              <path
                d="M0.453125 7.20312L6.82812 0.828125C7.29688 0.40625 8 0.40625 8.42188 0.828125L9.5 1.90625C9.92188 2.32812 9.92188 3.07812 9.5 3.5L4.95312 8L9.5 12.5469C9.92188 12.9688 9.92188 13.6719 9.5 14.1406L8.42188 15.1719C8 15.6406 7.29688 15.6406 6.82812 15.1719L0.453125 8.79688C0.03125 8.375 0.03125 7.67188 0.453125 7.20312Z"
                fill="currentColor"
              />
            </svg>
          </div>
        </div>

        <!-- Кнопка "Вправо" (следующая страница) -->
        <div
          class="teacher-courses__nav-btn"
          :class="{ 'teacher-courses__nav-btn--disabled': !hasNextPage }"
          @click="nextPage"
        >
          <img
            class="teacher-courses__nav-btn-bg"
            alt="Navigation button"
            src="/assets/animaapp/rectangle-86.svg"
          />
          <!-- Стрелка вправо -->
          <div class="teacher-courses__nav-btn-icon">
            <svg width="10" height="16" viewBox="0 0 10 16" fill="none">
              <path
                d="M9.5 8.79688L3.125 15.1719C2.65625 15.6406 1.95312 15.6406 1.53125 15.1719L0.453125 14.1406C0.03125 13.6719 0.03125 12.9688 0.453125 12.5469L5 8.04688L0.453125 3.5C0.03125 3.07812 0.03125 2.375 0.453125 1.90625L1.53125 0.828125C1.95312 0.40625 2.65625 0.40625 3.125 0.828125L9.5 7.20312C9.92188 7.67188 9.92188 8.375 9.5 8.79688Z"
                fill="currentColor"
              />
            </svg>
          </div>
        </div>
      </div>

      <!-- Кнопка "Создать курс" -->
      <div class="teacher-courses__create-btn" @click="handleCreateCourse">
        <div class="teacher-courses__create-btn-bg" />
        <div class="teacher-courses__create-btn-text">Создать курс</div>
      </div>

      <!-- Поле поиска -->
      <div class="teacher-courses__search">
        <div class="teacher-courses__search-bg" />
        <input
          v-model="searchQuery"
          type="text"
          class="teacher-courses__search-input"
          placeholder="Поиск"
        />
        <img
          class="teacher-courses__search-icon"
          alt="Search"
          src="@/assets/cdn-animaapp/Vector2.svg"
        />
      </div>

      <!-- Заголовок страницы -->
      <div class="teacher-courses__page-title">Ваши курсы</div>
    </div>

    <!-- Модальное окно подтверждения удаления курса -->
    <div
      v-if="showDeleteModal"
      class="delete-modal-overlay"
      @click.self="closeDeleteModal"
    >
      <div class="delete-modal">
        <div class="delete-modal__header">
          <h3 class="delete-modal__title">Подтверждение удаления</h3>
          <button
            class="delete-modal__close"
            @click="closeDeleteModal"
            aria-label="Закрыть"
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M18 6L6 18M6 6L18 18"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </button>
        </div>

        <div class="delete-modal__content">
          <div class="delete-modal__icon">
            <svg
              width="48"
              height="48"
              viewBox="0 0 48 48"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle cx="24" cy="24" r="24" fill="#FEE2E2" />
              <path
                d="M24 16V24M24 32H24.01"
                stroke="#DC2626"
                stroke-width="3"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>

          <p class="delete-modal__message">
            Вы уверены, что хотите удалить курс
            <strong>"{{ courseToDelete?.title }}"</strong>?
          </p>

          <p class="delete-modal__warning">
            Это действие удалит курс и все связанные с ним данные:
          </p>

          <ul class="delete-modal__list">
            <li>Все модули курса</li>
            <li>Все уроки и материалы</li>
            <li>Все тесты и задания</li>
            <li>Прогресс всех студентов</li>
          </ul>

          <div
            v-if="isDeleteConfirmationRequired"
            class="delete-modal__confirm"
          >
            <label class="delete-modal__label" for="delete-confirm-input"
              >Введите название курса для подтверждения</label
            >
            <input
              id="delete-confirm-input"
              v-model="deleteConfirmationInput"
              class="delete-modal__input"
              type="text"
              :placeholder="courseToDelete?.title || ''"
              autocomplete="off"
            />
            <p
              v-if="deleteConfirmationInput && !isDeleteConfirmationValid"
              class="delete-modal__error"
            >
              Название не совпадает
            </p>
          </div>

          <p class="delete-modal__irreversible">
            ⚠️ <strong>Это действие необратимо!</strong>
          </p>
        </div>

        <div class="delete-modal__actions">
          <button
            class="delete-modal__btn delete-modal__btn--cancel"
            @click="closeDeleteModal"
          >
            Отмена
          </button>
          <button
            class="delete-modal__btn delete-modal__btn--delete"
            @click="confirmDeleteCourse"
            :disabled="
              isDeleting ||
              (isDeleteConfirmationRequired && !isDeleteConfirmationValid)
            "
          >
            <span v-if="!isDeleting">Удалить курс</span>
            <span v-else>Удаление...</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Модальное окно управления курсом -->
    <CourseManagementModal
      :is-open="showManagementModal"
      :course-id="selectedCourseId"
      :course-data="selectedCourse"
      @close="handleCloseManagementModal"
      @element-updated="handleElementUpdated"
    />
  </div>
</template>

<script>
import { coursesService } from "@/services";
import CourseManagementModal from "@/components/CourseManagementModal.vue";
import { getImageUrl, getDefaultCourseImage } from "@/utils/imageUrl";
import eventBus from "@/utils/eventBus";
import { getCourseCategoryLabel } from "@/utils/courseCategories";
import { useAppStore } from "@/stores/useAppStore";
import { openInternalRouteInContext } from "@/utils/bitrixInternalNavigation";
import iconShow from "@/assets/cdn-animaapp/Vector.svg";
import iconHide from "@/assets/cdn-animaapp/invisible-1.svg";

export default {
  name: "TeacherCoursesList",
  components: {
    CourseManagementModal,
  },
  setup() {
    const appStore = useAppStore();
    return { appStore };
  },
  data() {
    return {
      currentPage: 0,
      itemsPerPage: 6,
      searchQuery: "",
      translateX: 0, // Смещение track-контейнера для свайпа (в процентах)
      loading: true,
      error: null,
      iconShow,
      iconHide,
      // Модальное окно управления курсом
      showManagementModal: false,
      selectedCourseId: null,
      selectedCourse: null,
      // Модальное окно удаления курса
      showDeleteModal: false,
      courseToDelete: null,
      isDeleting: false,
      deleteConfirmationInput: "",
      // Курсы загружаются из API
      courses: [],
    };
  },
  computed: {
    currentUser() {
      return this.appStore?.currentUser || null;
    },
    currentUserId() {
      const user = this.currentUser;
      return user?.id ?? user?.user_id ?? user?.userId ?? null;
    },
    isDeleteConfirmationRequired() {
      const user = this.currentUser;
      const course = this.courseToDelete;
      if (!user || !course) return false;
      const roles = Array.isArray(user.roles)
        ? user.roles
        : user.role
        ? [user.role]
        : [];
      const isTeacher = roles.includes("teacher");
      const isAdmin = roles.includes("admin");
      if (!isTeacher || isAdmin) return false;
      if (!course.teacher_id || !this.currentUserId) return false;
      return String(course.teacher_id) === String(this.currentUserId);
    },
    isDeleteConfirmationValid() {
      if (!this.isDeleteConfirmationRequired) return true;
      const expected = (this.courseToDelete?.title || "").trim();
      return (
        expected.length > 0 && this.deleteConfirmationInput.trim() === expected
      );
    },

    // Фильтрация курсов по поисковому запросу
    filteredCourses() {
      if (!this.searchQuery.trim()) {
        return this.courses;
      }

      const query = this.searchQuery.toLowerCase().trim();
      return this.courses.filter((course) => {
        const titleMatch = course.title.toLowerCase().includes(query);
        const categoryLabel = getCourseCategoryLabel(course.category);
        const categoryMatch =
          String(course.category).toLowerCase().includes(query) ||
          categoryLabel.toLowerCase().includes(query);
        return titleMatch || categoryMatch;
      });
    },
    // Разбивка курсов на страницы
    pages() {
      const pages = [];
      const totalCourses = this.filteredCourses.length;

      // Если курсов нет - возвращаем одну пустую страницу
      if (totalCourses === 0) {
        return [[]];
      }

      // Разбиваем на страницы
      for (let i = 0; i < totalCourses; i += this.itemsPerPage) {
        pages.push(this.filteredCourses.slice(i, i + this.itemsPerPage));
      }
      return pages;
    },

    // Общее количество страниц
    totalPages() {
      return this.pages.length;
    },
    // Есть ли следующая страница
    hasNextPage() {
      // Строгая проверка: есть ли страница после текущей
      return this.currentPage < this.totalPages - 1;
    },
    // Есть ли предыдущая страница
    hasPrevPage() {
      return this.currentPage > 0;
    },
    // Пустая ли текущая страница
    isCurrentPageEmpty() {
      return this.pages[this.currentPage]?.length === 0;
    },
  },
  watch: {
    searchQuery() {
      this.currentPage = 0;
      this.updateTransform();
    },

    // Сбрасываем на последнюю страницу, если текущая больше не существует
    totalPages(newTotal) {
      if (this.currentPage >= newTotal && newTotal > 0) {
        this.currentPage = newTotal - 1;
        this.updateTransform();
      }
    },
  },

  methods: {
    getCourseCategoryLabel,
    getRatingFillStyle(rating) {
      const normalizedInput =
        typeof rating === "string" ? rating.replace(",", ".").trim() : rating;
      const parsed = Number.parseFloat(normalizedInput);
      const value = Number.isFinite(parsed) ? parsed : 0;
      let normalized = value;
      if (value > 5 && value <= 10) {
        normalized = value / 2;
      } else if (value > 10) {
        normalized = value / 20;
      }
      const clamped = Math.max(0, Math.min(5, normalized));
      const percentage = (clamped / 5) * 100;
      return { "--rating-fill": `${percentage}%` };
    },
    getRatingStarType(rating, starIndex) {
      const normalizedInput =
        typeof rating === "string" ? rating.replace(",", ".").trim() : rating;
      const parsed = Number.parseFloat(normalizedInput);
      const value = Number.isFinite(parsed) ? parsed : 0;
      let normalized = value;
      if (value > 5 && value <= 10) {
        normalized = value / 2;
      } else if (value > 10) {
        normalized = value / 20;
      }
      const clamped = Math.max(0, Math.min(5, normalized));
      const fullStars = Math.floor(clamped);
      const hasHalf = clamped - fullStars >= 0.5;
      if (starIndex <= fullStars) return "full";
      if (starIndex === fullStars + 1 && hasHalf) return "half";
      return "empty";
    },
    // Обновление transform для свайпа (по образцу прототипа)
    updateTransform() {
      this.translateX = -this.currentPage * 100;
    },
    // Переход на следующую страницу
    nextPage() {
      // Переходим только если есть следующая страница
      if (this.hasNextPage) {
        this.currentPage++;
        this.updateTransform();
      }
    },

    // Переход на предыдущую страницу
    prevPage() {
      if (this.hasPrevPage) {
        this.currentPage--;
        this.updateTransform();
      }
    },

    // Методы для управления курсами
    handleCreateCourse() {
      this.$router.push("/teacher/courses/create");
    },
    handleSearch(query) {
      console.log("Поиск курсов:", query);
    },

    // Проверка, был ли курс создан админом
    isCreatedByAdmin(course) {
      // Показываем бейдж если:
      // 1. Курс создан админом для этого преподавателя
      // 2. Последнее действие - создание (last_action_type = 'created')
      return (
        course.created_by_role === "admin" &&
        course.created_by !== course.teacher_id &&
        course.last_action_type === "created"
      );
    },

    // Проверка, был ли курс переназначен админом
    isReassignedByAdmin(course) {
      // Показываем бейдж если:
      // 1. Последнее действие - переназначение (last_action_type = 'reassigned')
      // 2. Последнее изменение было сделано админом
      return (
        course.last_action_type === "reassigned" &&
        course.last_edited_by_role === "admin"
      );
    },

    // Проверка, был ли курс скрыт админом
    isHiddenByAdmin(course) {
      // Показываем бейдж если:
      // 1. Последнее действие - скрытие (last_action_type = 'hidden')
      // 2. Последнее изменение сделано админом
      // 3. Курс действительно скрыт
      return (
        course.last_action_type === "hidden" &&
        course.last_edited_by_role === "admin" &&
        course.is_hidden
      );
    },

    // Проверка, был ли курс опубликован (показан) админом
    isPublishedByAdmin(course) {
      // Показываем бейдж если:
      // 1. Последнее действие - показ (last_action_type = 'shown')
      // 2. Последнее изменение сделано админом
      // 3. Курс действительно не скрыт
      return (
        course.last_action_type === "shown" &&
        course.last_edited_by_role === "admin" &&
        !course.is_hidden
      );
    },

    // Проверка, был ли курс отредактирован админом
    isEditedByAdmin(course) {
      // Показываем бейдж если:
      // 1. Последнее действие - редактирование (last_action_type = 'edited')
      // 2. Последнее изменение было сделано админом
      // 3. И это изменение было сделано НЕ самим преподавателем
      return (
        course.last_action_type === "edited" &&
        course.last_edited_by_role === "admin" &&
        course.last_edited_by !== course.teacher_id
      );
    },

    // Форматирование даты редактирования
    formatEditDate(dateString) {
      if (!dateString) return "Неизвестно";
      const date = new Date(dateString);
      return date.toLocaleString("ru-RU", {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },

    openCourse(course) {
      if (!course?.id || course.is_hidden) return;
      this.$router.push(`/teacher/courses/${course.id}`);
    },

    handleManageCourse(courseId) {
      // Перенаправляем на страницу редактирования курса (вкладка "Модули")
      this.$router.push(`/teacher/courses/${courseId}/content`);
    },

    handleOpenCourseMap(course) {
      if (!course?.id) return;
      openInternalRouteInContext(this.$router, {
        path: `/teacher/courses/${course.id}/map`,
        query: { title: course.title || `#${course.id}` },
      }, {
        title: "Карта курса",
        leftBoundary: 40,
      });
    },

    handleCloseManagementModal() {
      this.showManagementModal = false;
      this.selectedCourseId = null;
      this.selectedCourse = null;
    },

    handleElementUpdated(data) {
      console.log("Элемент курса обновлен:", data);
      // TODO: Обновить данные курса при необходимости
      this.showNotification("Элемент успешно обновлен", "success");
    },

    // 🗑️ Методы для удаления курса
    handleDeleteCourse(courseId, courseTitle, teacherId) {
      console.log("Подтверждение удаления курса:", { courseId, courseTitle });
      this.courseToDelete = {
        id: courseId,
        title: courseTitle,
        teacher_id: teacherId,
      };
      this.deleteConfirmationInput = "";
      this.showDeleteModal = true;
    },

    closeDeleteModal() {
      this.showDeleteModal = false;
      this.courseToDelete = null;
      this.isDeleting = false;
      this.deleteConfirmationInput = "";
    },

    async confirmDeleteCourse() {
      if (!this.courseToDelete) return;

      if (
        this.isDeleteConfirmationRequired &&
        !this.isDeleteConfirmationValid
      ) {
        this.showNotification(
          "Введите точное название курса для подтверждения",
          "error"
        );
        return;
      }

      this.isDeleting = true;
      const courseId = this.courseToDelete.id;

      try {
        console.log("🗑️ Удаление курса из БД:", courseId);

        // Вызываем API для удаления курса
        await coursesService.delete(courseId);

        console.log("✅ Курс успешно удален из БД:", courseId);

        // Удаляем курс из локального массива
        const index = this.courses.findIndex((c) => c.id === courseId);
        if (index !== -1) {
          this.courses.splice(index, 1);
          console.log("✅ Курс удален из локального массива");
        }

        // Удаляем из localStorage
        const storedCourses = JSON.parse(
          localStorage.getItem("teacher_courses") || "[]"
        );
        const filteredCourses = storedCourses.filter((c) => c.id !== courseId);
        localStorage.setItem(
          "teacher_courses",
          JSON.stringify(filteredCourses)
        );

        // Закрываем модальное окно
        this.closeDeleteModal();

        // Показываем уведомление об успехе
        this.showNotification("Курс успешно удален", "success");

        // Если текущая страница стала пустой, переходим на предыдущую
        if (this.paginatedCourses?.length === 0 && this.currentPage > 0) {
          this.currentPage--;
          this.updateTransform();
        }

        this.isDeleting = false;
      } catch (error) {
        console.error("❌ Ошибка при удалении курса:", error);

        // Показываем уведомление об ошибке
        const errorMessage =
          error.response?.data?.message ||
          "Не удалось удалить курс. Попробуйте позже.";
        this.showNotification(errorMessage, "error");

        this.isDeleting = false;
      }
    },

    // ✅ Новый метод для переключения видимости
    async toggleCourseVisibility(courseId) {
      try {
        const course = this.courses.find((c) => c.id === courseId);
        if (!course) return;

        const newHiddenState = !course.is_hidden;

        // Отправляем запрос на сервер
        await coursesService.toggleVisibility(courseId, newHiddenState);

        // Обновляем локальное состояние
        course.is_hidden = newHiddenState;

        // Показываем уведомление
        this.showNotification(
          newHiddenState
            ? "Курс скрыт от студентов"
            : "Курс снова виден студентам",
          "success"
        );
      } catch (error) {
        console.error("Ошибка:", error);
        this.showNotification("Не удалось изменить видимость курса", "error");
      }
    },

    // ✅ Загрузка курсов из API
    async fetchCourses() {
      try {
        this.loading = true;
        this.error = null;

        console.log("🔍 Загрузка курсов преподавателя...");

        // 👇 СНАЧАЛА ПРОБУЕМ ЗАГРУЗИТЬ ИЗ API
        try {
          const apiResponse = await coursesService.getTeacherCourses();

          console.log("🔍 [fetchCourses] Полный ответ от API:", apiResponse);
          console.log("🔍 [fetchCourses] Тип ответа:", typeof apiResponse);

          const coursesData = this.normalizeCoursesResponse(apiResponse);

          console.log("📋 [fetchCourses] Курсы из API:", coursesData.length);

          // Преобразуем данные из API в формат компонента
          this.courses = coursesData.map((course) => {
            const imageUrl = getImageUrl(
              course.cover_image_url,
              getDefaultCourseImage()
            );
            console.log(
              `📸 Курс "${course.title}": cover_image_url="${course.cover_image_url}" → image="${imageUrl}"`
            );
            return {
              id: course.id,
              title: course.title,
              image: imageUrl,
              rating: parseFloat(course.rating || 0).toFixed(1),
              reviews: parseInt(course.reviews_count || 0),
              completionRate: Math.round(course.completion_rate || 0),
              homeworkRate: Math.round(course.homework_rate || 0),
              participants: parseInt(course.students_count || 0),
              category: course.category || "Без категории",
              modules: parseInt(course.modules_count || 0),
              type: course.category === "Битрикс" ? "bitrix" : "b2b",
              is_hidden: course.is_hidden || false,
              is_published: course.is_published || false,
              created_at: course.created_at,
              // Поля для отслеживания создания и редактирования
              teacher_id: course.teacher_id,
              created_by: course.created_by,
              created_by_role: course.created_by_role,
              last_edited_by: course.last_edited_by,
              last_edited_by_role: course.last_edited_by_role,
              last_edited_at: course.last_edited_at,
              last_visibility_action: course.last_visibility_action,
              last_action_type: course.last_action_type,
            };
          });

          // 👇 СОХРАНЯЕМ В LOCALSTORAGE
          this.saveCoursesToLocalStorage();

          console.log(
            "✅ Курсы преподавателя загружены из API:",
            this.courses.length
          );
        } catch (apiError) {
          console.warn(
            "⚠️ API недоступен, загружаем из localStorage:",
            apiError
          );

          // 👇 FALLBACK НА LOCALSTORAGE
          this.loadCoursesFromLocalStorage();
        }
      } catch (error) {
        console.error("❌ Ошибка загрузки курсов:", error);
        this.error = "Не удалось загрузить курсы";
        this.showNotification("Не удалось загрузить курсы", "error");
      } finally {
        this.loading = false;
      }
    },

    normalizeCoursesResponse(raw) {
      // Поддержка разных форматов: массив, {courses}, {data: {courses}}, {data}
      if (Array.isArray(raw)) return raw;
      const data = raw?.data ?? raw;
      if (Array.isArray(data)) return data;
      if (Array.isArray(data?.courses)) return data.courses;
      return [];
    },

    // ✅ Обработчик обновления курса
    async handleCourseUpdated(courseId) {
      console.log("📢 Получено событие обновления курса:", courseId);
      await this.fetchCourses();
      this.showNotification("Курс успешно обновлен", "success");
    },

    // ✅ Обработчик создания курса
    async handleCourseCreated(courseId) {
      console.log("📢 Получено событие создания курса:", courseId);
      await this.fetchCourses();
      this.showNotification("Курс успешно создан", "success");
    },

    // ✅ Обработка ошибок загрузки изображений
    handleImageError(event, course) {
      console.warn("⚠️ Ошибка загрузки изображения для курса:", course.id);
      // Устанавливаем изображение по умолчанию
      event.target.src = getDefaultCourseImage();
    },

    // 👇 СОХРАНЕНИЕ КУРСОВ В LOCALSTORAGE
    saveCoursesToLocalStorage() {
      try {
        const coursesKey = "teacher_courses_list";
        localStorage.setItem(coursesKey, JSON.stringify(this.courses));
        console.log("✅ Курсы сохранены в localStorage");
      } catch (error) {
        console.error("❌ Ошибка сохранения курсов в localStorage:", error);
      }
    },

    // 👇 ЗАГРУЗКА КУРСОВ ИЗ LOCALSTORAGE
    loadCoursesFromLocalStorage() {
      try {
        const coursesKey = "teacher_courses_list";
        const savedCourses = localStorage.getItem(coursesKey);

        if (savedCourses) {
          this.courses = JSON.parse(savedCourses);
          console.log(
            "✅ Курсы загружены из localStorage:",
            this.courses.length
          );
        } else {
          console.log("ℹ️ Нет сохраненных курсов в localStorage");
          this.courses = [];
        }
      } catch (error) {
        console.error("❌ Ошибка загрузки курсов из localStorage:", error);
        this.courses = [];
      }
    },

    // Вспомогательный метод для уведомлений
    showNotification(message, type = "info") {
      // Можно использовать toast библиотеку или простой alert
      // Для простоты используем console
      console.log(`[${type.toUpperCase()}] ${message}`);
      // TODO: Интегрировать с системой уведомлений (например, vue-toastification)
    },

    // Обновление высоты контейнера для адаптивности
    updateContainerHeight() {
      const container = this.$el?.querySelector(".teacher-courses__container");
      const gridWrapper = this.$el?.querySelector(
        ".teacher-courses__grid-wrapper"
      );

      if (container && gridWrapper) {
        // Получаем высоту grid-wrapper
        const gridHeight = gridWrapper.offsetHeight;
        // Получаем top позицию grid-wrapper
        const gridTop = 95; // Значение из CSS
        // Устанавливаем минимальную высоту контейнера
        const minHeight = gridTop + gridHeight + 40; // +40px отступ снизу
        container.style.minHeight = `${minHeight}px`;
      }
    },
  },

  mounted() {
    // Инициализация transform при монтировании (по образцу прототипа)
    this.updateTransform();
    // ✅ Загружаем курсы при монтировании
    this.fetchCourses();

    // Подписываемся на события обновления курсов
    eventBus.on("course-updated", this.handleCourseUpdated);
    eventBus.on("course-created", this.handleCourseCreated);

    // Устанавливаем адаптивную высоту контейнера
    this.$nextTick(() => {
      this.updateContainerHeight();
    });
  },

  updated() {
    // Обновляем высоту контейнера при изменении контента
    this.$nextTick(() => {
      this.updateContainerHeight();
    });
  },

  beforeUnmount() {
    // Отписываемся от событий
    eventBus.off("course-updated", this.handleCourseUpdated);
    eventBus.off("course-created", this.handleCourseCreated);
  },
};
</script>

<style scoped>
/* ============================================
   ОСНОВНОЙ КОНТЕЙНЕР
   ============================================ */

.teacher-courses {
  min-height: 600px; /* Минимальная высота вместо фиксированной */
  position: relative;
  width: 967px;
  font-family: "Poppins", sans-serif;
}

.teacher-courses__container {
  min-height: 600px; /* Минимальная высота вместо фиксированной */
  position: relative; /* Относительное позиционирование для адаптивности */
  width: 100%;
  padding-bottom: 40px; /* Отступ снизу для красоты */
}

/* ============================================
   ФОН
   ============================================ */

.teacher-courses__background {
  background-color: #dddddd66;
  border-radius: 20px;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 967px;
  /* Высота определяется контейнером автоматически */
}

/* ============================================
   СВАЙП КОНТЕЙНЕР (по образцу прототипа)
   ============================================ */

.teacher-courses__grid-wrapper {
  position: absolute;
  left: 18px;
  top: 95px;
  width: calc(100% - 36px);
  /* 👇 ИСПРАВЛЕНО: скрываем только по горизонтали */
  overflow-x: hidden;
  overflow-y: visible;
  touch-action: pan-y;
  /* 👇 ДОБАВЛЕНО: даём место для теней сверху и снизу */
  padding: 10px 0;
  margin: -10px 0;
}

.teacher-courses__grid-track {
  display: flex;
  transition: transform 0.6s ease; /* Плавная анимация свайпа */
  min-height: 400px; /* Минимальная высота для контента */
}

.teacher-courses__page {
  flex: 0 0 100%; /* Каждая страница занимает 100% ширины wrapper */
  padding: 0;
  min-height: 400px; /* Минимальная высота страницы */
}

/* ============================================
   СЕТКА КУРСОВ НА СТРАНИЦЕ
   ============================================ */

.teacher-courses__grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  width: 100%;
  /* 👇 ДОБАВЛЕНО: даём пространство для теней */
  padding: 8px;
  margin: -8px;
}

.teacher-courses__column {
  /* Не используется при grid layout */
  display: contents;
}

/* ============================================
   КАРТОЧКА КУРСА
   ============================================ */

.teacher-courses__card {
  background-color: #ffffff;
  border-radius: 20px;
  /* box-shadow: 0px 18.83px 47.08px #2f327d1a; */
  width: 100%;
  min-height: 450px;
  position: relative;
  display: flex;
  flex-direction: column;
  padding: 15px;
  border-radius: 20px;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  /*  Дополнительный отступ для уверенности */
  margin: 2px;
}

.teacher-courses__card:hover {
  cursor: pointer;
  transform: translateY(-5px);
  box-shadow: 0px 10px 15px #00000033;
}

/* Модификаторы карточек не нужны при grid layout */

/* ============================================
   СОДЕРЖИМОЕ КАРТОЧКИ
   ============================================ */

.teacher-courses__card-content,
.teacher-courses__card-content-alt {
  display: flex;
  flex-direction: column;
  gap: 10px;
  flex: 1;
  position: relative;
}

/* ============================================
   КНОПКА УПРАВЛЕНИЯ
   ============================================ */

.teacher-courses__manage-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 35px;
  padding: 0 20px;
  background-color: #0095eccc;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s ease;
  align-self: flex-start;
  margin-bottom: 5px;
  /* position: absolute;
	bottom: 0; */
}

.teacher-courses__manage-btn:hover {
  background-color: #0095ec;
  transform: scale(1.05);
}

.teacher-courses__manage-btn-bg {
  /* Не нужен - используем background у родителя */
  display: none;
}

.teacher-courses__manage-icon {
  width: 12px;
  height: 12px;
}

.teacher-courses__manage-text {
  color: #ffffff;
  font-family: "Poppins", Helvetica, sans-serif;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0;
  line-height: normal;
  white-space: nowrap;
}

/* ============================================
   РЕЙТИНГ
   ============================================ */

.teacher-courses__rating {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
}

.teacher-courses__rating-stars {
  display: inline-flex;
  align-items: center;
  height: 20px;
}
.teacher-courses__rating-stars-fill-svg {
  display: flex;
  align-items: center;
  gap: 4px;
  height: 100%;
}

.teacher-courses__rating-star {
  width: 20px;
  height: 20px;
  display: block;
  flex: 0 0 auto;
}

.teacher-courses__rating-score {
  color: #636363;
  font-family: "Poppins", Helvetica, sans-serif;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.28px;
  white-space: nowrap;
}

.teacher-courses__rating-reviews {
  color: #636363;
  font-family: "Poppins", Helvetica, sans-serif;
  font-size: 14px;
  font-weight: 400;
  letter-spacing: 0.28px;
  white-space: nowrap;
}

/* ============================================
   СТАТИСТИКА
   ============================================ */

.teacher-courses__stats {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 15px;
}

.teacher-courses__stats-completion,
.teacher-courses__stats-participants,
.teacher-courses__stats-category {
  color: #636363;
  font-family: "Poppins", Helvetica, sans-serif;
  font-size: 14px;
  font-weight: 400;
  letter-spacing: 0.28px;
  line-height: 1.4;
  margin: 0;
}

.teacher-courses__stats-participants {
  color: #00000080;
  font-size: 15px;
  letter-spacing: 0.3px;
}

.teacher-courses__stats-text {
  letter-spacing: 0.04px;
}

.teacher-courses__stats-divider {
  font-weight: 200;
  letter-spacing: 0.04px;
  margin: 0 5px;
}

/* ============================================
   НАЗВАНИЕ КУРСА
   ============================================ */

.teacher-courses__title {
  color: #000000;
  font-family: Helvetica, sans-serif;
  font-size: 17px;
  letter-spacing: 0;
  line-height: 1.5;
  margin: -15px 0 0 0;
  order: -1; /* Показываем название первым */
}

.teacher-courses__title--alt {
  font-family: "Roboto", Helvetica, sans-serif;
}

/* ============================================
   ИЗОБРАЖЕНИЕ КУРСА
   ============================================ */

.teacher-courses__image {
  width: 100%;
  height: 180px;
  object-fit: cover;
  border-radius: 10px;
  margin-bottom: 15px;
  order: -2; /* Показываем изображение первым */
}

/* Модификаторы изображений не нужны - общие стили применяются из .teacher-courses__image */

/* ============================================
   КНОПКА "СКРЫТЬ"
   ============================================ */

.teacher-courses__hide-btn {
  /* position: absolute; */
  /* top: 400px; */
  /* right: 15px; */
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 5px 10px;
  cursor: pointer;
  transition: opacity 0.2s ease;
  background: transparent;
  border: none;
}

.teacher-courses__hide-btn:hover {
  opacity: 0.7;
}

.teacher-courses__hide-text {
  color: #00000099;
  font-family: "Poppins", Helvetica, sans-serif;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.28px;
  line-height: normal;
}

.teacher-courses__hide-icon {
  width: 15px;
  height: 10px;
}

/* ============================================
   НАВИГАЦИЯ (СТРЕЛКИ)
   ============================================ */

.teacher-courses__navigation {
	display: flex;
	gap: 10px;
	height: 30px;
	left: 878px;
	position: absolute;
	top: 22px;
	width: 72px;
	z-index: 2;
}

.teacher-courses__nav-btn {
	height: 30px;
	position: relative;
	width: 32px;
	cursor: pointer;
	transition: transform 0.2s ease, opacity 0.2s ease;
	background-color: #0096ed;
	border-radius: 10px;
}

.teacher-courses__nav-btn:hover {
  transform: scale(1.1);
}

.teacher-courses__nav-btn--disabled {
  opacity: 0.4;
  cursor: not-allowed;
  pointer-events: none;
}

.teacher-courses__nav-btn--disabled:hover {
  transform: none;
}

.teacher-courses__nav-btn-bg {
	aspect-ratio: 1;
	height: 30px;
	left: 0;
	position: absolute;
	top: 0;
	width: 30px;
	opacity: 0;
}

.teacher-courses__nav-btn-icon {
  position: absolute;
  left: 11px;
  top: 6px;
  width: 10px;
  height: 16px;
  color: #ffffff;
}

/* ============================================
   КНОПКА "СОЗДАТЬ КУРС"
   ============================================ */

.teacher-courses__create-btn {
  height: 35px;
  left: 607px;
  position: absolute;
  top: 20px;
  width: 194px;
  cursor: pointer;
  transition: transform 0.2s ease;
}

.teacher-courses__create-btn:hover {
  transform: scale(1.05);
}

.teacher-courses__create-btn-bg {
  background-color: #0095ec;
  border-radius: 10px;
  height: 35px;
  left: 0;
  position: absolute;
  top: 0;
  width: 192px;
  transition: background-color 0.2s ease;
}

.teacher-courses__create-btn:hover .teacher-courses__create-btn-bg {
  background-color: #0085d4;
}

.teacher-courses__create-btn-text {
  color: #ffffff;
  font-family: "Poppins", Helvetica, sans-serif;
  font-size: 14px;
  font-weight: 600;
  left: 47px;
  letter-spacing: 0.28px;
  line-height: normal;
  position: absolute;
  top: 7px;
}

/* ============================================
   ПОЛЕ ПОИСКА
   ============================================ */

.teacher-courses__search {
  height: 30px;
  left: 238px;
  position: absolute;
  top: 22px;
  width: 329px;
}

.teacher-courses__search-bg {
  background-color: #7e7e7e33;
  border-radius: 12px;
  height: 30px;
  left: 0;
  position: absolute;
  top: 0;
  width: 327px;
  transition: background-color 0.2s ease;
}

.teacher-courses__search:hover .teacher-courses__search-bg {
  background-color: #7e7e7e44;
}

.teacher-courses__search-input {
  position: absolute;
  left: 50px;
  top: 0;
  width: calc(100% - 60px);
  height: 100%;
  background: transparent;
  border: none;
  outline: none;
  color: #000000;
  font-family: "Poppins", Helvetica, sans-serif;
  font-size: 15px;
  font-weight: 400;
  letter-spacing: 0.6px;
  padding: 0;
}

.teacher-courses__search-input::placeholder {
  color: #00000080;
}

.teacher-courses__search-icon {
  aspect-ratio: 1;
  height: 15px;
  left: 27px;
  position: absolute;
  top: 9px;
  width: 15px;
}

/* ============================================
   ЗАГОЛОВОК СТРАНИЦЫ
   ============================================ */

.teacher-courses__page-title {
  color: #000000;
  font-family: "Poppins", Helvetica, sans-serif;
  font-size: 25px;
  font-weight: 700;
  left: 23px;
  letter-spacing: 0;
  line-height: normal;
  position: absolute;
  top: 18px;
  white-space: nowrap;
  width: 160px;
}

/* ============================================
   АДАПТИВНОСТЬ И МЕДИА-ЗАПРОСЫ
   ============================================ */

@media (max-width: 1024px) {
  .teacher-courses {
    width: 100%;
    height: auto;
  }

  .teacher-courses__container {
    width: 100%;
    position: relative;
  }

  .teacher-courses__background {
    width: 100%;
  }

  .teacher-courses__grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .teacher-courses__grid-wrapper {
    top: 90px; /* Чуть меньший отступ для планшетов */
    width: calc(100% - 36px);
  }
}

@media (max-width: 768px) {
  .teacher-courses__grid {
    grid-template-columns: 1fr;
  }

  .teacher-courses__navigation,
  .teacher-courses__create-btn,
  .teacher-courses__search {
    position: static;
    margin: 10px auto;
  }

  .teacher-courses__grid-wrapper {
    position: relative; /* На мобильных возвращаем relative, так как верхние элементы в потоке */
    top: auto;
    left: auto;
    margin-top: 20px; /* Минимальный отступ, так как верхние элементы в потоке документа */
    width: 100%;
    padding: 0 18px;
  }
}

/* ============================================
   АНИМАЦИИ
   ============================================ */

@keyframes teacher-courses-fade-in {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.teacher-courses__card {
  animation: teacher-courses-fade-in 0.3s ease;
}

/* ============================================
   СОСТОЯНИЯ ЗАГРУЗКИ И ОШИБОК (опционально)
   ============================================ */

.teacher-courses__loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 600px;
  gap: 14px;
  font-family: "Poppins", Helvetica, sans-serif;
  font-size: 18px;
  color: #636363;
}

.teacher-courses__loading-spinner {
  width: 46px;
  height: 46px;
  border-radius: 50%;
  border: 4px solid #dbeafe;
  border-top-color: #0095ed;
  animation: teacher-courses-spin 0.8s linear infinite;
}

.teacher-courses__loading-text {
  font-family: "Poppins", Helvetica, sans-serif;
  font-size: 18px;
  font-weight: 500;
  color: #636363;
}

@keyframes teacher-courses-spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.teacher-courses__error {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  font-family: "Poppins", Helvetica, sans-serif;
  font-size: 16px;
  color: #ff4757;
}

.teacher-courses__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 600px;
  gap: 24px;
}

.teacher-courses__empty-text {
  font-family: "Poppins", Helvetica, sans-serif;
  font-size: 42px;
  font-weight: 500;
  color: #d1d5db;
  letter-spacing: 0.5px;
}

.teacher-courses__empty-icon {
  width: 500px;
  height: 500px;
  opacity: 0.1;
  filter: grayscale(100%) brightness(1.5);
}

/* ============================================
   СКРЫТЫЕ КАРТОЧКИ
   ============================================ */

.teacher-courses__card--hidden {
  opacity: 0.6;
  background: linear-gradient(135deg, #f5f5f5 0%, #e0e0e0 100%);
  border: 2px dashed #999999;
  position: relative;
  cursor: not-allowed;
}

.teacher-courses__card--hidden:hover {
  transform: none;
  box-shadow: none;
}

.teacher-courses__card--hidden::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: repeating-linear-gradient(
    45deg,
    transparent,
    transparent 10px,
    rgba(0, 0, 0, 0.02) 10px,
    rgba(0, 0, 0, 0.02) 20px
  );
  pointer-events: none;
  border-radius: 20px;
}

/* Бейдж "Скрыт" */
.teacher-courses__hidden-badge {
  position: absolute;
  top: 15px;
  right: 15px;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background-color: #ff4757;
  color: white;
  border-radius: 8px;
  font-family: "Poppins", Helvetica, sans-serif;
  font-size: 12px;
  font-weight: 600;
  z-index: 10;
  box-shadow: 0 2px 8px rgba(255, 71, 87, 0.3);
}

.teacher-courses__hidden-badge svg {
  width: 14px;
  height: 14px;
}

/* Бейдж "Создано админом" */
.teacher-courses__admin-created-badge {
  position: absolute;
  top: 15px;
  left: 15px;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
  color: white;
  border-radius: 8px;
  font-family: "Poppins", Helvetica, sans-serif;
  font-size: 11px;
  font-weight: 600;
  z-index: 10;
  box-shadow: 0 2px 8px rgba(17, 153, 142, 0.4);
  cursor: help;
  transition: all 0.3s ease;
}

.teacher-courses__admin-created-badge:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(17, 153, 142, 0.6);
}

/* Бейдж "Переназначено админом" */
.teacher-courses__admin-reassigned-badge {
  position: absolute;
  top: 15px;
  left: 15px;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
  color: white;
  border-radius: 8px;
  font-family: "Poppins", Helvetica, sans-serif;
  font-size: 11px;
  font-weight: 600;
  z-index: 10;
  box-shadow: 0 2px 8px rgba(240, 147, 251, 0.4);
  cursor: help;
  transition: all 0.3s ease;
}

.teacher-courses__admin-reassigned-badge:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(240, 147, 251, 0.6);
}

/* Бейдж "Скрыто админом" */
.teacher-courses__admin-hidden-badge {
  position: absolute;
  top: 15px;
  left: 15px;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: linear-gradient(135deg, #ffa751 0%, #ffe259 100%);
  color: #333;
  border-radius: 8px;
  font-family: "Poppins", Helvetica, sans-serif;
  font-size: 11px;
  font-weight: 600;
  z-index: 10;
  box-shadow: 0 2px 8px rgba(255, 167, 81, 0.4);
  cursor: help;
  transition: all 0.3s ease;
}

.teacher-courses__admin-hidden-badge:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(255, 167, 81, 0.6);
}

/* Бейдж "Опубликовано админом" */
.teacher-courses__admin-published-badge {
  position: absolute;
  top: 15px;
  left: 15px;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
  color: white;
  border-radius: 8px;
  font-family: "Poppins", Helvetica, sans-serif;
  font-size: 11px;
  font-weight: 600;
  z-index: 10;
  box-shadow: 0 2px 8px rgba(17, 153, 142, 0.4);
  cursor: help;
  transition: all 0.3s ease;
}

.teacher-courses__admin-published-badge:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(17, 153, 142, 0.6);
}

/* Бейдж "Изменено админом" */
.teacher-courses__admin-edited-badge {
  position: absolute;
  top: 15px;
  left: 15px;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 8px;
  font-family: "Poppins", Helvetica, sans-serif;
  font-size: 11px;
  font-weight: 600;
  z-index: 10;
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.4);
  cursor: help;
  transition: all 0.3s ease;
}

.teacher-courses__admin-edited-badge:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.6);
}

/* Затемнение изображения у скрытых курсов */
.teacher-courses__card--hidden .teacher-courses__image {
  filter: grayscale(100%) brightness(0.7);
}

/* Стили для кнопки скрыть/показать */
.teacher-courses__card--hidden .teacher-courses__hide-btn {
  background-color: #4caf50;
  border-radius: 8px;
  padding: 8px 12px;
}

.teacher-courses__card--hidden .teacher-courses__hide-text {
  color: #ffffff;
  font-weight: 600;
}

/* Карточка и контент */
.teacher-courses__card {
  background: #fff;
  border-radius: 16px;
  padding: 14px;
  min-height: 360px;
  display: flex;
}
.teacher-courses__card-content {
  display: flex;
  flex-direction: column;
  flex: 1;
}

/* Картинка */
.teacher-courses__image {
  width: 100%;
  height: 180px;
  object-fit: cover;
  border-radius: 12px;
  margin-bottom: 12px;
}

/* Заголовок */
.teacher-courses__title {
  margin: 2px 0 6px;
  font-size: 16px;
  font-weight: 700;
  line-height: 1.3;
}

/* Метаданные */
.teacher-courses__meta {
  margin: 0 0 4px;
  color: #5c5c5c;
  font-size: 14px;
}
.teacher-courses__meta--muted {
  color: #00000080;
}
.teacher-courses__meta-divider {
  margin: 0 6px;
  color: #9b9b9b;
}

/* Статистика */
.teacher-courses__stats-line {
  margin: 6px 0 10px;
  color: #5c5c5c;
  font-size: 14px;
}

/* Рейтинг */
.teacher-courses__rating {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 2px;
}
.teacher-courses__rating-stars {
  display: inline-flex;
  align-items: center;
  height: 18px;
}
.teacher-courses__rating-stars-fill-svg {
  display: flex;
  align-items: center;
  gap: 4px;
  height: 100%;
}

.teacher-courses__rating-star {
  width: 18px;
  height: 18px;
  display: block;
  flex: 0 0 auto;
}
.teacher-courses__rating-count {
  color: #636363;
  font-weight: 600;
  font-size: 14px;
}
.teacher-courses__rating-reviews {
  color: #636363;
  font-size: 14px;
}

/* Футер с кнопками (прижат к низу) */
.teacher-courses__footer {
  margin-top: auto;
  display: grid;
  grid-template-columns: 1fr auto;
  grid-template-rows: auto auto;
  align-items: start;
  padding-top: 10px;
}

/* “Скрыть” */
.teacher-courses__hide-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: transparent;
  border: none;
  padding: 6px 4px;
  cursor: pointer;
  grid-column: 1;
  grid-row: 1;
  justify-self: start;
}

.teacher-courses__map-btn {
  grid-column: 1;
  grid-row: 2;
  justify-self: start;
  border: 1px solid #d0d7de;
  border-radius: 8px;
  background: #f6f8fa;
  color: #1f2328;
  font-size: 12px;
  font-weight: 600;
  line-height: 1;
  padding: 8px 10px;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;
}
.teacher-courses__map-btn:hover {
  background: #eef2f7;
  border-color: #bfc7d1;
}
.teacher-courses__hide-icon {
  width: 16px;
  height: 12px;
}
.teacher-courses__hide-text {
  color: #00000099;
  font-weight: 600;
  font-size: 14px;
}

/* “Управление” */
.teacher-courses__manage-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: #0095ed;
  color: #fff;
  border: none;
  border-radius: 10px;
  height: 35px;
  padding: 0 16px;
  cursor: pointer;
  transition: transform 0.15s ease, background 0.15s ease;
}
.teacher-courses__manage-btn:hover {
  transform: translateY(-1px);
  background: #0085d4;
}
.teacher-courses__manage-icon {
  width: 12px;
  height: 12px;
}
.teacher-courses__manage-text {
  font-size: 13px;
  font-weight: 700;
}

/* Убираем абсолюты, чтобы кнопки не “улетали” */
.teacher-courses__manage-btn,
.teacher-courses__hide-btn,
.teacher-courses__map-btn {
  position: static;
}

/* Когда курс скрыт — зелёная плашка у “Показать” */
.teacher-courses__card--hidden
  .teacher-courses__footer
  .teacher-courses__hide-btn {
  background: #4caf50;
  border-radius: 8px;
  padding: 8px 10px;
}
.teacher-courses__card--hidden
  .teacher-courses__footer
  .teacher-courses__hide-text {
  color: #fff;
}

/* ============================================
   КНОПКИ ДЕЙСТВИЙ (Удалить + Управление)
   ============================================ */

.teacher-courses__action-buttons {
  display: flex;
  flex-direction: column;
  gap: 10px;
  align-items: flex-end;
  grid-column: 2;
  grid-row: 1 / span 2;
  align-self: start;
}

/* Кнопка "Удалить" */
.teacher-courses__delete-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  background: #fee2e2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  color: #dc2626;
  font-family: "Poppins", Helvetica, sans-serif;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.teacher-courses__delete-btn:hover {
  background: #fecaca;
  border-color: #fca5a5;
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(220, 38, 38, 0.2);
}

.teacher-courses__delete-btn:active {
  transform: translateY(0);
  box-shadow: 0 1px 4px rgba(220, 38, 38, 0.2);
}

.teacher-courses__delete-icon {
  width: 14px;
  height: 14px;
  flex-shrink: 0;
}

.teacher-courses__delete-text {
  font-size: 13px;
  font-weight: 600;
}

/* ============================================
   МОДАЛЬНОЕ ОКНО УДАЛЕНИЯ
   ============================================ */

.delete-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  animation: fadeIn 0.2s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.delete-modal {
  background: white;
  border-radius: 16px;
  padding: 32px;
  max-width: 500px;
  width: 90%;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  animation: slideUp 0.3s ease;
}

@keyframes slideUp {
  from {
    transform: translateY(20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.delete-modal__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.delete-modal__title {
  font-family: "Poppins", Helvetica, sans-serif;
  font-size: 24px;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
}

.delete-modal__close {
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  color: #6b7280;
  transition: color 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.delete-modal__close:hover {
  color: #1f2937;
}

.delete-modal__content {
  margin-bottom: 24px;
}

.delete-modal__icon {
  display: flex;
  justify-content: center;
  margin-bottom: 20px;
}

.delete-modal__message {
  font-family: "Poppins", Helvetica, sans-serif;
  font-size: 16px;
  color: #374151;
  margin: 0 0 16px 0;
  text-align: center;
  line-height: 1.5;
}

.delete-modal__message strong {
  color: #1f2937;
  font-weight: 600;
}

.delete-modal__warning {
  font-family: "Poppins", Helvetica, sans-serif;
  font-size: 14px;
  color: #6b7280;
  margin: 16px 0 8px 0;
  font-weight: 500;
}

.delete-modal__list {
  list-style: none;
  padding: 0;
  margin: 0 0 16px 0;
}

.delete-modal__list li {
  font-family: "Poppins", Helvetica, sans-serif;
  font-size: 14px;
  color: #6b7280;
  padding: 4px 0 4px 24px;
  position: relative;
}

.delete-modal__list li::before {
  content: "•";
  position: absolute;
  left: 8px;
  color: #dc2626;
  font-weight: bold;
}

.delete-modal__confirm {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 12px;
}

.delete-modal__label {
  font-family: "Poppins", Helvetica, sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: #374151;
}

.delete-modal__input {
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  font-family: "Poppins", Helvetica, sans-serif;
  font-size: 14px;
  color: #111827;
}

.delete-modal__input:focus {
  outline: none;
  border-color: #dc2626;
  box-shadow: 0 0 0 2px #fee2e2;
}

.delete-modal__error {
  margin: 0;
  font-family: "Poppins", Helvetica, sans-serif;
  font-size: 12px;
  color: #dc2626;
}

.delete-modal__irreversible {
  font-family: "Poppins", Helvetica, sans-serif;
  font-size: 14px;
  color: #dc2626;
  margin: 16px 0 0 0;
  text-align: center;
  padding: 12px;
  background: #fef2f2;
  border-radius: 8px;
  border: 1px solid #fee2e2;
}

.delete-modal__actions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
}

.delete-modal__btn {
  padding: 12px 24px;
  border-radius: 8px;
  font-family: "Poppins", Helvetica, sans-serif;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
}

.delete-modal__btn--cancel {
  background: #f3f4f6;
  color: #374151;
}

.delete-modal__btn--cancel:hover {
  background: #e5e7eb;
}

.delete-modal__btn--delete {
  background: #dc2626;
  color: white;
}

.delete-modal__btn--delete:hover:not(:disabled) {
  background: #b91c1c;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(220, 38, 38, 0.3);
}

.delete-modal__btn--delete:active:not(:disabled) {
  transform: translateY(0);
  box-shadow: 0 2px 6px rgba(220, 38, 38, 0.3);
}

.delete-modal__btn--delete:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>
